/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { useState, useEffect } from 'react';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, ColumnDef, flexRender } from '@tanstack/react-table';
import { Lead, getLeads, deleteLead, setLead, runAiProbeAsJob } from '../data';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from 'styled-components';
import { Table } from 'react-bootstrap';
import Badge from 'react-bootstrap/Badge';

function AiLeads({ }) {
    const theme : any = useTheme();
    const [ rowSelection, setRowSelection ] = useState({});
    const [ globalFilter, setGlobalFilter ] = useState('');
    const [ data, setData ] = useState<Lead[]>([]);
    const [ refresh, setRefresh ] = useState<boolean>(true);
    const [ busy, setBusy ] = useState<string | null>(null);

    useEffect(() => {
        if (!refresh) return;
        const fetchData = async () => {
            try {
                let leads = await getLeads();
                setData(leads);
            } catch (error) {
                console.error('Error fetching AI leads:', error);
            }
            setRefresh(false);
        };
        fetchData();
    }, [refresh]);

    const columns: ColumnDef<Lead>[] = [
        {
            id: 'select',
            header: ({ table }) => (
            <Form.Check
                type="checkbox"
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
            />
            ),
            cell: ({ row }) => (
                <Form.Check
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
        },
        {
            header: 'Computed PVKey',
            accessorKey: 'pvkey.pvkey',
            filterFn: 'includesString',
            size: 40,
        },
        {
            header: 'Product',
            accessorKey: 'product',
            filterFn: 'includesString',
        },
        {
            header: 'Vendor',
            accessorKey: 'vendor',
            filterFn: 'includesString',
        },
        {
            header: 'Vendor Lcl',
            accessorKey: 'vendorLocal',
            filterFn: 'includesString',
        },
        {
            header: 'Title',
            accessorKey: 'title',
            filterFn: 'includesString',
        },
        {
            header: 'Notes',
            accessorKey: 'notes',
            filterFn: 'includesString',
        },
        {
            header: 'Country',
            accessorKey: 'country',
            filterFn: 'includesString',
        },
        {
            header: 'Website',
            accessorKey: 'website',
            filterFn: 'includesString',
        },
        {
            header: "Categories",
            accessorKey: 'categories',
            filterFn: (row, id, filterValue) => {
                const rowValue = row.getValue(id);
                if (Array.isArray(rowValue)) {
                    return rowValue.some((val: string) =>
                        val.toLowerCase().includes(filterValue.toLowerCase())
                    );
                }
                return false;
            },
            cell: (props : any) => {
                const row = props.row.original;
                return (
                    <span>{row.categories ? row.categories.join(', ') : ''}</span>
                );
            }
        },
        {
            id: 'actions',
            header: 'Actions',
            size: 20,
            cell: (props : any) => {
                const row = props.row.original;
                return (
                    <div className="d-flex gap-2 justify-content-center">
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                                async function runit() {
                                    await deleteLead(row.id);
                                    setRefresh(true);
                                }
                                runit();
                            }}
                            style={{
                                backgroundColor: 'transparent',
                                borderColor: theme.colors.danger,
                                color: theme.colors.danger
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                );
            }
        },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: 'includesString',
        enableGlobalFilter: true,
        enableMultiRowSelection: true,
        onRowSelectionChange: setRowSelection as any,
        getRowId: (row: any) => row.id.toString(),
        defaultColumn: {
            size: 20,
        },
        state: {
            rowSelection,
            globalFilter
        }
    });

    function handleDelete(id: number) {
        console.log(`Delete lead ID=${id}`);
    }

    return (
        <Container>
            <Row className="pt-3">
                <Col md={6}>
                    <Button
                        variant="outline-primary"
                        style={{
                            borderColor: theme.colors.ctext,
                            color: theme.colors.ctext
                        }}
                        className="ms-4 me-2"
                        disabled={busy !== null}
                        onClick={() => {
                            async function runit() {
                                setBusy('Loading from probes…');
                                try {
                                    await runAiProbeAsJob('probe_to_lead', {});
                                    setRefresh(true);
                                } finally {
                                    setBusy(null);
                                }
                            }
                            runit();
                        }}
                    >
                        {busy === 'Loading from probes…' ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" /> : null}
                        Load From Probes
                    </Button>
                    <Button
                        variant="outline-primary"
                        style={{
                            borderColor: theme.colors.ctext,
                            color: theme.colors.ctext
                        }}
                        className="me-2"
                        disabled={busy !== null}
                        onClick={() => {
                            async function runit() {
                                await deleteLead(-1);
                                setRefresh(true);
                            }
                            runit();
                        }}
                    >
                        Delete All
                    </Button>
                    <Button
                        variant="outline-primary"
                        title="Attempt to create fingerprints from leads"
                        style={{
                            borderColor: theme.colors.ctext,
                            color: theme.colors.ctext
                        }}
                        disabled={busy !== null}
                        onClick={() => {
                            async function runit() {
                                setBusy('Creating fingerprints…');
                                try {
                                    await runAiProbeAsJob('lead_to_finger', {});
                                    setRefresh(true);
                                } finally {
                                    setBusy(null);
                                }
                            }
                            runit();
                        }}
                    >
                        {busy === 'Creating fingerprints…' ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" /> : null}
                        Create Fingerprints
                    </Button>
                </Col>
                <Col md={4}>
                    <div className="mb-3">
                        <input
                            value={globalFilter ?? ''}
                            onChange={e => {
                                let newValue = String(e.target.value);
                                setGlobalFilter(newValue);
                                table.setGlobalFilter(newValue);
                            }}
                            className="form-control"
                            placeholder="Search all columns..."
                            style={{ 
                                backgroundColor: theme.colors.bg, 
                                color: theme.colors.ctext,
                                borderColor: theme.colors.border
                            }}
                        />
                    </div>
                </Col>
                <Col md={2} className="pe-3 text-end">
                    { table.getFilteredRowModel().rows.length !== table.getCoreRowModel().rows.length ? (
                        <>
                            <Badge pill bg="success" className="me-4">
                                {table.getFilteredRowModel().rows.length} Shown
                            </Badge>
                            <Badge pill bg="primary" className="me-1">
                                {table.getCoreRowModel().rows.length} Total
                            </Badge>
                        </>
                    ) : (
                        <Badge pill bg="success" className="me-4">
                            {table.getFilteredRowModel().rows.length} Leads
                        </Badge>
                    )}
                </Col>
            </Row>
            <Row className="ps-4 pe-4">
                <Col md={12} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    <Table hover variant="dark" size="sm">
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>{headerGroup.headers.map(header => (
                                    <th key={header.id} colSpan={header.colSpan} >
                                        <div onClick={header.column.getToggleSortingHandler()}>
                                            {header.isPlaceholder ? null : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            ) }
                                            {header.column.getIsSorted() ? (
                                                <span style={{ marginLeft: '5px' }}>
                                                    <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                                </span>
                                            ) : null }
                                        </div>
                                    </th>
                                ))}</tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => (
                                <tr key={row.id} className={row.getIsSelected() ? 'table-active' : ''}>
                                    {row.getVisibleCells().map(cell => (
                                        <td style={{ overflowWrap: 'anywhere' }}
                                            key={cell.id} >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>
    );
}

export default AiLeads;